import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Modal, ModalProps, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Form, Formik, useFormikContext, useField } from 'formik';
import { map, round } from 'lodash';
import { FormGroupControl, FormGroupSelect, FormGroupControlProps, RowItem } from '@components/Base';
import { HealthClaimCostItem } from '@models/HealthClaimCostItem';
import { PolicyBenefitLimit } from '@models/PolicyBenefitLimit';
import { mergeInitialValues } from '@utils/formik';
import { formatPercentage } from '@utils/currency';
import { healthClaimCostItemSchema, FormValues } from './schema';

const GROUP_PROPS = { className: 'mb-3' };

const OverLimitAmountFormGroupControl = ({ name, ...props }: FormGroupControlProps) => {
  const {
    values: { policyBenefitLimitId, declaredAmount, approvedAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field] = useField(name);

  useEffect(() => {
    if (policyBenefitLimitId === '') {
      setFieldValue(name, 0);
      return;
    }

    if (touched.declaredAmount && touched.approvedAmount && !errors.approvedAmount && !errors.declaredAmount) {
      const overLimitAmount = Number(declaredAmount) - Number(approvedAmount);
      const roundedOverLimitAmount = round(overLimitAmount, 2);
      setFieldValue(name, Math.max(roundedOverLimitAmount, 0).toFixed(2));
    }
  }, [declaredAmount, approvedAmount, touched.declaredAmount, touched.approvedAmount, setFieldValue, name, errors]);

  return <FormGroupControl {...field} {...props} />;
};

const DeductibleCopayAmountFormGroupControl = ({
  policyBenefitLimits,
  name,
  ...props
}: { policyBenefitLimits: PolicyBenefitLimit[] } & FormGroupControlProps) => {
  const {
    values: { policyBenefitLimitId, declaredAmount, approvedAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field] = useField(name);
  const [helpText, setHelpText] = useState('');
  const [selectedPolicyBenefitLimit, setSelectedPolicyBenefitLimit] = useState<PolicyBenefitLimit | null>(null);

  useEffect(() => {
    setSelectedPolicyBenefitLimit(
      policyBenefitLimits.find((limit) => limit.id === Number(policyBenefitLimitId)) || null,
    );
  }, [policyBenefitLimitId]);

  useEffect(() => {
    if (!selectedPolicyBenefitLimit) {
      setFieldValue(name, 0);
      return;
    }

    if (touched.declaredAmount && !errors.declaredAmount) {
      if (selectedPolicyBenefitLimit.copayPercentage > 0) {
        const copayAmount = (Number(declaredAmount) * selectedPolicyBenefitLimit.copayPercentage) / 100;
        const roundedCopayAmount = round(copayAmount, 2);
        setFieldValue(name, roundedCopayAmount.toFixed(2));
      } else {
        setFieldValue(name, Number(selectedPolicyBenefitLimit.deductibleAmount).toFixed(2));
      }
    }
  }, [selectedPolicyBenefitLimit, declaredAmount, touched.declaredAmount, setFieldValue, name, errors]);

  useEffect(() => {
    if (Number(selectedPolicyBenefitLimit?.copayPercentage) > 0) {
      setHelpText(`Co-pay: ${formatPercentage(selectedPolicyBenefitLimit?.copayPercentage, 2)}`);
    } else if (Number(selectedPolicyBenefitLimit?.deductibleAmount) > 0) {
      setHelpText(`Deductible amount: ${Number(selectedPolicyBenefitLimit?.deductibleAmount).toFixed(2)}`);
    } else {
      setHelpText('');
    }
  }, [selectedPolicyBenefitLimit]);

  return <FormGroupControl {...field} {...props} help={helpText} />;
};

const UncoveredExpensesAmountFormGroupControl = ({ name, ...props }: FormGroupControlProps) => {
  const {
    values: { customSubBenefit, declaredAmount, approvedAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field] = useField(name);

  useEffect(() => {
    if (customSubBenefit.trim() === '') {
      setFieldValue(name, 0);
      return;
    }

    if (touched.declaredAmount && touched.approvedAmount && !errors.declaredAmount && !errors.approvedAmount) {
      const uncoveredExpensesAmount = Number(declaredAmount) - Number(approvedAmount);
      const roundedUncoveredExpensesAmount = round(uncoveredExpensesAmount, 2);
      setFieldValue(name, roundedUncoveredExpensesAmount.toFixed(2));
    }
  }, [
    customSubBenefit,
    declaredAmount,
    approvedAmount,
    touched.declaredAmount,
    touched.approvedAmount,
    setFieldValue,
    name,
    errors,
  ]);

  return <FormGroupControl {...field} {...props} />;
};

const TotalCoveredAmountFormGroupControl = ({ name, ...props }: FormGroupControlProps) => {
  const {
    values: { policyBenefitLimitId, declaredAmount, approvedAmount, deductibleCopayAmount },
    touched,
    errors,
    setFieldValue,
  } = useFormikContext<FormValues>();
  const [field] = useField(name);

  useEffect(() => {
    if (touched.approvedAmount && !errors.approvedAmount) {
      const amountAfterDeductibleCopayAmount = Number(declaredAmount) - Number(deductibleCopayAmount);
      const roundedAmountAfterDeductibleCopayAmount = round(amountAfterDeductibleCopayAmount, 2);
      const totalCoveredAmount = !!policyBenefitLimitId
        ? roundedAmountAfterDeductibleCopayAmount > Number(approvedAmount)
          ? Number(approvedAmount)
          : roundedAmountAfterDeductibleCopayAmount
        : Number(approvedAmount);
      setFieldValue(name, Math.max(totalCoveredAmount, 0).toFixed(2));
    }
  }, [
    declaredAmount,
    approvedAmount,
    deductibleCopayAmount,
    touched.approvedAmount,
    policyBenefitLimitId,
    setFieldValue,
    name,
    errors,
  ]);

  return <FormGroupControl {...field} {...props} />;
};

type Props = ModalProps & {
  onSubmit: (values: FormValues) => void;
  healthClaimCostItem: HealthClaimCostItem | null;
  policyBenefitLimits: PolicyBenefitLimit[];
  hospitalRoomAndBoardStay: number;
  icuStay: number;
};

const CostItemFormModal = ({
  onSubmit,
  policyBenefitLimits,
  healthClaimCostItem,
  hospitalRoomAndBoardStay,
  icuStay,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const initialValues: FormValues = mergeInitialValues(
    healthClaimCostItemSchema.getDefault(),
    { policyBenefitLimitId: healthClaimCostItem?.policyBenefitLimit?.id },
    healthClaimCostItem,
  );

  return (
    <Modal {...props} size="lg" centered>
      <Formik initialValues={initialValues} validationSchema={healthClaimCostItemSchema} onSubmit={onSubmit}>
        {({ isSubmitting }) => {
          return (
            <Form>
              <Modal.Header closeButton>
                <Modal.Title>Cost Item</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Alert variant="info">
                  <RowItem
                    label={`Hospital room and board stay (Nights/Days)`}
                    value={hospitalRoomAndBoardStay}
                    labelProps={{ sm: 6, md: 6 }}
                  />
                  <RowItem label={`ICU stay (Nights/Days)`} value={icuStay} labelProps={{ sm: 6, md: 6 }} />
                </Alert>

                <Row>
                  <Col>
                    <FormGroupSelect name="policyBenefitLimitId" label={`Sub benefit*`} groupProps={GROUP_PROPS} asRow>
                      <option value="">Please select</option>
                      {map(policyBenefitLimits, ({ id, subBenefit }) => (
                        <option key={subBenefit} value={id}>
                          {subBenefit}
                        </option>
                      ))}
                    </FormGroupSelect>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      asRow
                      name="customSubBenefit"
                      label={`Custom sub benefit`}
                      help={`If the sub benefit you want is not listed, you can specify it here.`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      required
                      name="declaredAmount"
                      label={`Amount declared`}
                      groupProps={GROUP_PROPS}
                      asRow
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl
                      required
                      name="approvedAmount"
                      label={`Approved amount (cover limit)`}
                      groupProps={GROUP_PROPS}
                      asRow
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <UncoveredExpensesAmountFormGroupControl
                      required
                      name="uncoveredExpensesAmount"
                      label={`Expenses not covered`}
                      groupProps={GROUP_PROPS}
                      asRow
                      disabled
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <DeductibleCopayAmountFormGroupControl
                      policyBenefitLimits={policyBenefitLimits}
                      required
                      asRow
                      disabled
                      name="deductibleCopayAmount"
                      label={`Deductible or co-pay amount`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <OverLimitAmountFormGroupControl
                      required
                      asRow
                      disabled
                      name="overLimitAmount"
                      label={`Over limit amount`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TotalCoveredAmountFormGroupControl
                      required
                      asRow
                      disabled
                      name="totalCoveredAmount"
                      label={`Total amount covered`}
                      groupProps={GROUP_PROPS}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroupControl name="remark" label={`Remark`} groupProps={GROUP_PROPS} asRow />
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide}>
                  {t('confirmModal.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {t('common:submit')}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export { CostItemFormModal };
